// ============================================================================
// HomeServices — Cold-outreach campaign landing page
// Route: /home-services-photography
// Audience: HVAC, plumbing, electrical, roofing, and home-service companies
// in Phoenix, Scottsdale, and across Arizona. Sibling to
// /aesthetic-clinic-photography — same architecture, components, and styling,
// retargeted to the trades.
//
// Page is NOT in top nav. Crawlable (robots: index, follow). Linked from
// /business via a small tile inserted between Use Cases and Selected
// Client Work in BusinessArizona.jsx, mirroring the clinic tile pattern.
//
// Primary CTA = "Book a Call" → Calendly intro-call link (new tab) in hero
// + final CTA. No phone number on the page. Card-level CTAs continue to
// route through /inquire with `source=homeservices-*` for attribution.
//
// Reused infrastructure (no rebuilds — shipping speed):
//   - <HeroVideo>          — Phase 11.3 three-layer architecture
//   - <Section> primitives — Phase 10 spacing scale
//   - <MediaGallery>       — masonry + PhotoSwipe lightbox
//   - <TestimonialCard>    — backdrop-aware, 4-line layout
//   - <VideoGallerySection> — Phase 15 auto-hide video section
//   - useTestimonials("business") — max 3 on this page (no trades tag yet)
//   - useHomeServicesSelectedWork() — page's own editable Sanity gallery
//     (slug 'home-services-selected-work'). Falls back synchronously to
//     HOME_SERVICES_GALLERY_ITEMS (rekeyed deep-copy of the business
//     gallery) so the section never renders empty pre-seed.
//   - useHomeServicesVideo()    — auto-hides until Eli publishes ≥1 clip
//     on the Sanity `home-services-video` doc.
//   - useHomeServicesHero()     — Sanity-driven hero. Null until the
//     `home-services-hero` heroMedia doc is published; HeroVideo falls
//     back to the shared business placeholder in the meantime.
//
// Copy is hardcoded per the campaign brief for shipping speed.
// ============================================================================

import React, { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { SEO } from "@/lib/seoConfig";
import { Section, Kicker, SerifTitle, HairlineBrass } from "@/components/Section";
import { HeroVideo, heroTextShadow } from "@/components/HeroVideo";
import { MediaGallery } from "@/components/MediaGallery";
import { TestimonialCard } from "@/components/TestimonialCard";
import { VideoGallerySection } from "@/components/VideoGallerySection";
import { Separator } from "@/components/ui/separator";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
    useTestimonials,
    useHomeServicesSelectedWork,
    useHomeServicesVideo,
    useHomeServicesHero,
} from "@/lib/content";

// ─── Constants ──────────────────────────────────────────────────────────────

// Calendly intro-call link — primary "Book a Call" CTA target in hero +
// final CTA. Opens in a new tab. Per brief: 15-minute phone intro; no
// phone number anywhere else on the page.
const CALENDLY_URL = "https://calendly.com/eli-vonstuben/intro-call";

// ─── Page-level checklist (Section 3) ──────────────────────────────────────

const INCLUDED_ITEMS = [
    "Owner, team, and technician portraits",
    "Trucks, fleet, and branded equipment",
    "Crew on the job and finished installs",
    "Before / after and quality-of-work shots",
    "Google Business Profile & Local Services Ads images",
    "Website, landing page, and review imagery",
    "Short vertical clips for social and reels",
    "Recruiting visuals to help you hire",
    "Consistent visual direction across all of it",
];

// ─── Packages (Section 5) ──────────────────────────────────────────────────
//
// TWO cards side-by-side on desktop, stacked on mobile. Photo + Video is
// the recommended/featured card (RECOMMENDED pill + brass ring + shadow).
// The single differentiator between the two is video deliverables. Visual
// style mirrors the clinic page's PackageCard exactly so visitors who hit
// both pages get a consistent interaction.
const PACKAGES = [
    {
        id: "photo-video",
        name: "Field & Brand — Photo + Video",
        badge: "Recommended",
        regular: "$2,300",
        promo: "$1,850",
        promoTotalLabel: "$1,850 total",
        paymentLines: [
            "$925 reserves your shoot date",
            "$925 balance due before the shoot",
        ],
        bestFor:
            "Best for a company that wants a full photo library plus short video for website, Google, ads, and social.",
        includes: [
            "Half-day on-site session · up to 2 nearby locations",
            "Owner, team, and technician portraits",
            "Trucks, fleet, equipment, and finished-work photos",
            "Before / after and quality-of-work images",
            "Edited company overview video (up to 90 seconds)",
            "3 vertical clips / reels (15–30 sec each)",
            "10–15 short raw social clips",
            "Images sized for website, Google profile & Local Services Ads",
            "Consistent visual direction across the full set",
        ],
        photoOnlyNote: null,
        cta: "Reserve Your Session",
        source: "homeservices-pv",
    },
    {
        id: "photo-day",
        name: "Field & Brand — Photo Day",
        badge: null,
        regular: "$1,450",
        promo: "$1,050",
        promoTotalLabel: "$1,050 total",
        paymentLines: [
            "$525 reserves your shoot date",
            "$525 balance due before the shoot",
        ],
        bestFor:
            "Built for Arizona home-service brands that want to look established the moment a homeowner lands on their site — a focused photo session with real creative direction behind it.",
        includes: [
            "Up to 2 hours on site · 1 location",
            "Pre-shoot creative direction call — together we shape the look, locations, and shot list around your brand",
            "A defined visual identity — consistent color, tone, and styling so every image looks like one established company, not a stock-photo patchwork",
            "Owner, team, and technician portraits that earn trust on sight",
            "Trucks, fleet, and branded-equipment shots that show you're the real local operation",
            "Detail and quality-of-work images that make homeowners feel confident calling you",
            "Edited in a clean, Arizona-bright style that reads premium on every screen",
            "Every image sized and cropped for your website, Google Business Profile & Local Services Ads",
            "Fast edited-gallery delivery",
        ],
        photoOnlyNote: "Photography only — no video",
        cta: "Reserve Your Session",
        source: "homeservices-photo",
    },
];

// ─── Card component ────────────────────────────────────────────────────────
//
// Mirrors the clinic page's PackageCard exactly:
//   - Promo price (large oxblood serif) + struck regular price beside it
//   - SUMMER PROMO PRICE kicker label below
//   - 1–2 payment lines (reserve + balance)
//   - Best-for line
//   - Collapsible "Includes" on <md, always-open on md+
//   - Optional photo-only italic note below the list
//   - Full-width CTA at the bottom

function PackageCard({ pkg }) {
    const [includesOpen, setIncludesOpen] = useState(false);
    const panelId = `hs-pkg-includes-${pkg.id}`;
    const inquireHref = `/inquire?source=${encodeURIComponent(pkg.source)}`;

    return (
        <article
            data-testid={`hs-package-${pkg.id}`}
            data-badge={pkg.badge || "false"}
            className={cn(
                "relative flex flex-col h-full bg-background border rounded-[3px] p-6 sm:p-7 transition-all",
                pkg.badge
                    ? "border-[hsl(var(--lux-oxblood))] shadow-[0_24px_60px_-30px_rgba(74,24,32,0.35)] ring-1 ring-[hsl(var(--lux-oxblood))]/15"
                    : "border-border hover:border-foreground/40"
            )}
        >
            {pkg.badge ? (
                <span
                    data-testid={`hs-package-badge-${pkg.id}`}
                    className="absolute -top-3 left-6 inline-flex items-center bg-[hsl(var(--lux-oxblood))] text-[hsl(var(--lux-porcelain))] text-[10px] tracking-[0.22em] uppercase px-3 py-1.5 rounded-full"
                >
                    {pkg.badge}
                </span>
            ) : null}

            <h3 className="font-serif-display text-2xl leading-tight mt-1">
                {pkg.name}
            </h3>

            {/* Price block — promo total primary (large oxblood serif), regular
              * struck through (small muted) beside it, "SUMMER PROMO PRICE"
              * label below. Matches /business + clinic promo cards exactly. */}
            <div className="mt-3 flex items-baseline gap-3">
                <span
                    data-testid={`hs-package-promo-${pkg.id}`}
                    className="text-[hsl(var(--lux-oxblood))] font-serif-display text-2xl"
                >
                    {pkg.promoTotalLabel}
                </span>
                <span
                    data-testid={`hs-package-regular-${pkg.id}`}
                    className="text-xs tracking-[0.04em] text-muted-foreground line-through"
                >
                    {pkg.regular}
                </span>
            </div>
            <div
                data-testid={`hs-package-promo-label-${pkg.id}`}
                className="mt-1 text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--lux-brass))]"
            >
                Summer promo price
            </div>

            {/* Payment lines — 2 lines for both cards (reserve + balance) */}
            <div className="mt-3 space-y-1 text-[12.5px] tracking-[0.02em] text-muted-foreground">
                {pkg.paymentLines.map((line, idx) => (
                    <div
                        key={line}
                        data-testid={`hs-package-payment${idx + 1}-${pkg.id}`}
                    >
                        {line}
                    </div>
                ))}
            </div>

            <Separator className="my-5 bg-[hsl(var(--lux-brass))]/45 w-8" />

            <div className="kicker">Best for</div>
            <p className="mt-1.5 text-[13px] leading-[1.7] text-foreground/85">
                {pkg.bestFor}
            </p>

            {/* What's included — collapsible on mobile (<md), always-open on
              * md+. Brass-check pills inside, matching clinic + business. */}
            <div className="mt-5 border-t border-border/60 pt-4 md:border-t-0 md:pt-0">
                <button
                    type="button"
                    onClick={() => setIncludesOpen((v) => !v)}
                    aria-expanded={includesOpen}
                    aria-controls={panelId}
                    data-testid={`hs-package-includes-toggle-${pkg.id}`}
                    className="flex items-center justify-between w-full text-left md:cursor-default md:pointer-events-none"
                >
                    <span className="kicker">What&apos;s included</span>
                    <ChevronDown
                        aria-hidden
                        className={cn(
                            "h-4 w-4 text-muted-foreground transition-transform duration-200 md:hidden",
                            includesOpen && "rotate-180"
                        )}
                    />
                </button>
                <div
                    id={panelId}
                    data-testid={`hs-package-included-${pkg.id}`}
                    className={cn(
                        "overflow-hidden transition-[max-height,opacity] duration-200 ease-out",
                        includesOpen ? "max-h-[1400px] opacity-100" : "max-h-0 opacity-0",
                        "md:!max-h-none md:!opacity-100"
                    )}
                >
                    <ul className="mt-3 space-y-2 text-[13px]">
                        {pkg.includes.map((it) => (
                            <li key={it} className="flex items-start gap-2">
                                <Check
                                    aria-hidden
                                    className="h-3.5 w-3.5 mt-1.5 text-[hsl(var(--lux-brass))] shrink-0"
                                />
                                <span className="leading-[1.6]">{it}</span>
                            </li>
                        ))}
                    </ul>
                    {pkg.photoOnlyNote ? (
                        <p
                            data-testid={`hs-package-photo-only-note-${pkg.id}`}
                            className="mt-3 text-[12px] italic text-muted-foreground"
                        >
                            {pkg.photoOnlyNote}
                        </p>
                    ) : null}
                </div>
            </div>

            <div className="mt-auto pt-6">
                <Link
                    to={inquireHref}
                    data-testid={`hs-package-cta-${pkg.id}`}
                    onClick={() => {
                        if (typeof window !== "undefined") {
                            window.scrollTo({ top: 0, behavior: "instant" });
                        }
                    }}
                    className={cn(
                        "btn-request btn-request--sm w-full justify-center",
                        !pkg.badge && "btn-request--ghost"
                    )}
                >
                    {pkg.cta}
                </Link>
            </div>
        </article>
    );
}

// ─── Page ──────────────────────────────────────────────────────────────────

export default function HomeServices() {
    // Reuse business-tagged testimonials (no trades tag exists yet).
    // Cap to 3 to keep page momentum; hides cleanly when empty.
    const allBusinessTestimonials = useTestimonials("business");
    const testimonials = (allBusinessTestimonials || []).slice(0, 3);

    // Page's own editable stills gallery — falls back to the rekeyed
    // home-services SWR fallback while the Sanity doc is unseeded.
    const galleryItems = useHomeServicesSelectedWork();

    // Slug-driven video gallery — auto-hides until Eli publishes a clip.
    const videoItems = useHomeServicesVideo();

    // Sanity-driven hero. When `home-services-hero` heroMedia doc is
    // unpublished/missing, returns null → HeroVideo falls back to the
    // business placeholder video.
    const heroMedia = useHomeServicesHero();

    return (
        <div className="relative" data-testid="home-services-page">
            <Seo {...SEO.homeServices} />

            {/* ─── 1. HERO ─────────────────────────────────────────────── */}
            <HeroVideo
                dataTestId="hs-hero"
                media={heroMedia}
                ariaLabel="Photography and video for HVAC, plumbing, electrical, roofing, and home-service companies in Arizona by Elizaveta von Stuben Imagery."
            >
                <span
                    className="block font-sans uppercase text-[11px] sm:text-[12px]"
                    style={{
                        color: "#e6e1db",
                        letterSpacing: "0.22em",
                        ...heroTextShadow.light,
                    }}
                    data-testid="hs-hero-eyebrow"
                >
                    HVAC &middot; Plumbing &middot; Electrical &middot; Roofing &mdash; Arizona
                </span>
                <h1
                    className="font-serif-display mt-3 sm:mt-4 text-[clamp(31px,7.6vw,34px)] sm:text-[clamp(46px,5.2vw,76px)]"
                    data-testid="hs-hero-headline"
                    style={{
                        color: "#fbf8f4",
                        lineHeight: 1.04,
                        letterSpacing: "0",
                        maxWidth: "820px",
                        ...heroTextShadow.heavy,
                    }}
                >
                    Photography &amp; Video for Arizona Home-Service Companies
                </h1>
                <p
                    className="mt-4 sm:mt-6 text-[15px] sm:text-[17px] leading-[1.55]"
                    data-testid="hs-hero-supporting"
                    style={{
                        color: "#f5f1eb",
                        maxWidth: "680px",
                        ...heroTextShadow.medium,
                    }}
                >
                    Your crew, your trucks, and your finished work &mdash;
                    shot to build instant trust. Before a homeowner picks
                    up the phone, they look you up. Make sure what they
                    see says established, local, and ready.
                </p>
                <div className="mt-6 sm:mt-8 flex flex-wrap gap-3">
                    <a
                        href={CALENDLY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hs-hero-book-call"
                        className="btn-request"
                        style={{ boxShadow: "0 10px 30px rgba(0,0,0,0.35)" }}
                    >
                        Book a Call
                    </a>
                    <Link
                        to="/inquire?source=homeservices-landing"
                        data-testid="hs-hero-reserve"
                        className="btn-request btn-request--sm btn-request--outline-light"
                        style={{
                            borderColor: "rgba(251,248,244,0.65)",
                            color: "#fbf8f4",
                            boxShadow: "0 6px 22px rgba(0,0,0,0.25)",
                        }}
                    >
                        Reserve Your Session
                    </Link>
                    <a
                        href="#hs-packages"
                        data-testid="hs-hero-view-packages"
                        className="btn-request btn-request--sm btn-request--outline-light"
                        style={{
                            borderColor: "rgba(251,248,244,0.45)",
                            color: "#fbf8f4",
                            boxShadow: "0 6px 22px rgba(0,0,0,0.18)",
                            background: "transparent",
                        }}
                    >
                        View Packages
                    </a>
                </div>
            </HeroVideo>

            {/* ─── 2. PROBLEM / OPPORTUNITY ────────────────────────────── */}
            <Section
                spacing="normal"
                className="bg-[hsl(var(--lux-porcelain))] border-t border-border"
                data-testid="hs-opportunity"
            >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                    <div className="lg:col-span-5">
                        <Kicker>The opportunity</Kicker>
                        <SerifTitle className="mt-3">
                            They check you out before they call.
                        </SerifTitle>
                        <HairlineBrass className="mt-6" />
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <p className="text-base leading-[1.95] text-foreground/85 max-w-[60ch]">
                            Homeowners decide who to trust in the first
                            few seconds on your website, Google profile,
                            and reviews. Real images of your team, trucks,
                            and completed work make your company look
                            established and safe to invite in &mdash; and
                            give your whole online presence the same
                            quality as the work you do.
                        </p>
                    </div>
                </div>
            </Section>

            {/* ─── 3. WHAT ONE SESSION CREATES ─────────────────────────── */}
            <Section spacing="normal" data-testid="hs-included">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
                    <div className="lg:col-span-5">
                        <Kicker>What&apos;s included</Kicker>
                        <SerifTitle className="mt-3">
                            One day on site. A full content library.
                        </SerifTitle>
                        <HairlineBrass className="mt-6" />
                        <p className="mt-6 text-base leading-[1.9] text-foreground/85 max-w-[55ch]">
                            One planned shoot gives your company a
                            working library of photos and short clips you
                            can use everywhere a customer finds you.
                        </p>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <ul className="space-y-3">
                            {INCLUDED_ITEMS.map((item) => (
                                <li
                                    key={item}
                                    data-testid="hs-included-item"
                                    className="flex items-start gap-3 text-[14px] sm:text-[15px] leading-[1.7] text-foreground/85"
                                >
                                    <Check
                                        className="h-4 w-4 mt-1 text-[hsl(var(--lux-brass))] shrink-0"
                                        aria-hidden
                                    />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </Section>

            {/* ─── 4a. STILLS GALLERY ──────────────────────────────────── *
              * Reads its own Sanity doc (slug 'home-services-selected-work').
              * SWR fallback ships the rekeyed business gallery items so the
              * section is never empty pre-seed; Eli will swap to trades
              * photos in Studio.
              */}
            <Section
                spacing="normal"
                className="bg-[hsl(var(--lux-porcelain))] border-t border-border"
                data-testid="hs-gallery"
            >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-10">
                    <div className="lg:col-span-7">
                        <Kicker>Selected work</Kicker>
                        <SerifTitle className="mt-3">
                            Real work, ready to publish.
                        </SerifTitle>
                    </div>
                    <p className="lg:col-span-5 text-sm leading-[1.85] text-muted-foreground max-w-[52ch]">
                        Team, trucks, workspace, and finished-work details
                        &mdash; the kind of images that make a homeowner
                        feel confident calling you.
                    </p>
                </div>
                {galleryItems && galleryItems.length > 0 ? (
                    <MediaGallery
                        items={galleryItems}
                        initialCount={8}
                        incrementCount={8}
                        desktopColumns={3}
                        galleryId="hs-gallery"
                        defaultInquiryType="Home-Service Content Session"
                    />
                ) : null}
            </Section>

            {/* ─── 4b. VIDEO (auto-hides until published) ──────────────── */}
            <VideoGallerySection
                items={videoItems}
                testId="hs-video"
                galleryId="hs-video-gallery"
                inquiryType="Home-Service Content Session"
                desktopColumns={3}
                className="border-t border-border"
            />

            {/* ─── 5. PACKAGES ─────────────────────────────────────────── */}
            <Section
                spacing="normal"
                id="hs-packages"
                data-testid="hs-packages"
                className="scroll-mt-24"
            >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-6">
                    <div className="lg:col-span-7">
                        <Kicker>Investment</Kicker>
                        <SerifTitle className="mt-3">
                            Two ways to start.
                        </SerifTitle>
                    </div>
                    <p className="lg:col-span-5 text-sm leading-[1.85] text-muted-foreground max-w-[52ch]">
                        Both packages are built around how your company
                        actually uses content &mdash; website, Google
                        profile, Local Services Ads, and social.
                    </p>
                </div>

                <p
                    data-testid="hs-packages-lead-in"
                    className="mb-10 text-[15px] sm:text-base leading-[1.7] text-foreground/85 max-w-[68ch]"
                >
                    Choose the session that gives your company a fresh
                    photo and video library for your website, Google
                    profile, ads, and social.
                </p>

                {/* 2-card grid. Recommended card (Photo + Video) renders
                  * first so it appears at the top on mobile stack. Visual
                  * flagging via the oxblood pill + ring + border. */}
                <div
                    data-testid="hs-packages-grid"
                    className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-8 max-w-[920px] mx-auto"
                >
                    {PACKAGES.map((p) => (
                        <PackageCard key={p.id} pkg={p} />
                    ))}
                </div>

                {/* Availability / scarcity line — quiet supporting type
                  * below the cards, above the custom-session note. */}
                <p
                    data-testid="hs-availability-line"
                    className="mt-8 sm:mt-10 text-center text-[13px] leading-[1.7] text-muted-foreground max-w-[68ch] mx-auto"
                >
                    A limited number of June home-service content
                    sessions are open across Phoenix and Scottsdale.
                    Reserve a date to hold your spot.
                </p>

                {/* Custom-session note band ─ subtle, centered */}
                <div
                    data-testid="hs-custom-note"
                    className="mt-8 sm:mt-10 mx-auto max-w-[640px] text-center"
                >
                    <p className="text-[14px] sm:text-[15px] leading-[1.8] text-foreground/85">
                        <strong className="font-medium">
                            Need something more specific?
                        </strong>{" "}
                        I can tailor the session scope, deliverables, and
                        pricing around what your company actually needs
                        &mdash; whether that&apos;s only team portraits,
                        only truck and fleet shots, only short clips, or
                        a larger multi-crew content day. Reach out and
                        we&apos;ll shape it together.
                    </p>
                    <Link
                        to="/inquire?source=homeservices-custom"
                        data-testid="hs-custom-cta"
                        className="mt-5 inline-flex items-center text-[11px] tracking-[0.18em] uppercase lux-underline text-foreground hover:text-[hsl(var(--lux-oxblood))]"
                    >
                        Ask about a custom session
                    </Link>
                </div>
            </Section>

            {/* ─── 6. TESTIMONIALS ──────────────────────────────────────── *
              * Business-tagged Sanity docs (no trades tag yet). Hides
              * cleanly if Sanity returns empty.
              */}
            {testimonials.length > 0 ? (
                <section
                    data-testid="hs-testimonials"
                    className="border-t border-border"
                >
                    <Section spacing="normal">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-10 sm:mb-12">
                            <div className="lg:col-span-7">
                                <Kicker>What clients say</Kicker>
                                <SerifTitle className="mt-3">
                                    Calm, professional, and on brand.
                                </SerifTitle>
                            </div>
                            <p className="lg:col-span-5 text-sm leading-[1.85] text-muted-foreground max-w-[52ch]">
                                A small set of clients who&apos;ve worked
                                with the studio for brand and content
                                photography &mdash; in their own words.
                            </p>
                        </div>
                        <div
                            className={[
                                "grid gap-6 sm:gap-8",
                                testimonials.length === 1
                                    ? "grid-cols-1 max-w-[640px]"
                                    : testimonials.length === 2
                                        ? "grid-cols-1 md:grid-cols-2"
                                        : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
                            ].join(" ")}
                        >
                            {testimonials.map((r) => (
                                <TestimonialCard
                                    key={r.id}
                                    testimonial={r}
                                    testId={`hs-testimonial-${r.id}`}
                                />
                            ))}
                        </div>
                    </Section>
                </section>
            ) : null}

            {/* ─── 7. FINAL CTA ─────────────────────────────────────────── */}
            <Section
                spacing="loose"
                data-testid="hs-final-cta"
                className="border-t border-border bg-[hsl(var(--lux-porcelain))]"
            >
                <div className="mx-auto max-w-[760px] text-center">
                    <Kicker>Next step</Kicker>
                    <SerifTitle className="mt-3">
                        Make your business look as good as your work.
                    </SerifTitle>
                    <HairlineBrass className="mt-6 mx-auto" />
                    <p className="mt-6 text-base sm:text-[17px] leading-[1.85] text-foreground/85 max-w-[58ch] mx-auto">
                        Book a quick call and we&apos;ll map out the
                        shoot &mdash; or send the form and I&apos;ll
                        reply with the best next step.
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <a
                            href={CALENDLY_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hs-final-book-call"
                            className="btn-request"
                        >
                            Book a Call
                        </a>
                        <Link
                            to="/inquire?source=homeservices-final"
                            data-testid="hs-final-reserve"
                            onClick={() => {
                                if (typeof window !== "undefined") {
                                    window.scrollTo({ top: 0, behavior: "instant" });
                                }
                            }}
                            className="btn-request btn-request--sm btn-request--ghost"
                        >
                            Reserve Your Session
                        </Link>
                    </div>
                </div>
            </Section>
        </div>
    );
}
